import {
  IComponent,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IProperty,
  EPropertyType
} from "@kuika/kuika-cl-model";

/**
 * One value prop a SetValueOf action may target ("Prop name to set"). `name` is what the action
 * stores in `multipleValuePropName` and what the runtime dispatches on, so it is always the raw
 * metadata prop name.
 */
export interface ISettableValueProp {
  name: string;
  selectedType: EPropertyType;
  /** The designer-facing label when the definition gives one. */
  tagName: string;
}

/**
 * The value props SetValueOf may target on a component instance (KOA-18575).
 *
 * The component DEFINITION is the source of truth: an instance persisted before the definition
 * gained a value prop (LocationSearch `address` / `latitude` / `longitude`) must still offer it, and
 * a value prop the definition has since deprecated (LocationSearch `value`) must stop being offered —
 * except when the action already targets it, so an existing action keeps showing what it does.
 * Instance-only value props (dynamic widget props) are appended after the definition's.
 */
export function collectSettableValueProps(
  item: IDisplayItemComponent | undefined,
  definition: IComponent | undefined,
  currentlySelected: string | undefined
): ISettableValueProp[] {
  const result: ISettableValueProp[] = [];
  const seen: Set<string> = new Set<string>();

  const push = (name: string, selectedType: EPropertyType, tagName: string | undefined): void => {
    if (seen.has(name)) return;
    seen.add(name);
    result.push({ name, selectedType, tagName: tagName ?? "" });
  };

  const instanceProperties: IDisplayItemProperty[] = item?.properties ?? [];
  const definitionProperties: IProperty[] = definition?.properties ?? [];

  for (const definitionProperty of definitionProperties) {
    if (definitionProperty.isValueProp !== true) continue;
    if (definitionProperty.isDeprecated === true && definitionProperty.name !== currentlySelected) continue;
    const instanceProperty: IDisplayItemProperty | undefined = instanceProperties.find(
      (p: IDisplayItemProperty): boolean => p.name === definitionProperty.name
    );
    push(
      definitionProperty.name,
      instanceProperty?.selectedType ?? definitionProperty.selectedType,
      definitionProperty.tagName
    );
  }

  for (const instanceProperty of instanceProperties) {
    const dynamicDefinition = instanceProperty.dynamicPropertyDefinition;
    const isDynamicValueProp: boolean =
      instanceProperty.isDynamicProperty === true && dynamicDefinition?.isValueProp === true;
    if (!isDynamicValueProp) continue;
    if (dynamicDefinition?.isDeprecated === true && instanceProperty.name !== currentlySelected) continue;
    push(instanceProperty.name, instanceProperty.selectedType, dynamicDefinition?.tagName);
  }

  return result;
}
